import { brandPalette } from "../../../constants/brandPalette";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  FormLabel,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  RangeSlider,
  RangeSliderFilledTrack,
  RangeSliderThumb,
  RangeSliderTrack,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import { MdCheck, MdClose } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import {
  adminModalBodyProps,
  adminModalContentProps,
  adminModalHeaderProps,
  adminSelectStyles,
} from "../../../components/Models/admin-modal-theme";
import { examSubmissionPdfUrl } from "../../../components/student/PdfPreviewModal";
import { getMarkSchemesBySubjectRequest } from "../../../modules/admin-modules/mark-schemes/Actions";
import { markPaperWithAi } from "../../../modules/admin-modules/mark-schemes/Api";
import { buildMarkPaperBody } from "../../../modules/admin-modules/mark-schemes/grader-models";
import { GraderModelFields, useGraderModelSelection } from "./grading-model-field";
import { toast } from "react-toastify";

const DEFAULT_PAGE_RANGE = [1, 20];

const modalSelectStyles = {
  ...adminSelectStyles(),
  menuPortal: (base) => ({ ...base, zIndex: 1600 }),
};

function isRegisteredScheme(scheme) {
  const id = scheme?.grader_scheme_id;
  return id !== null && id !== undefined && id !== "" && id !== 0 && id !== "0";
}

function normalizeAiPayload(data) {
  if (!data || typeof data !== "object") return null;
  if (Array.isArray(data.questions)) return data;
  if (data.data && Array.isArray(data.data.questions)) return data.data;
  return null;
}

function studentLabel(row) {
  const name = row?.student?.name;
  const code = row?.student?.user_name;
  if (name && code) return `${name} (${code})`;
  return name || code || `Submission #${row?.id}`;
}

function statusMeta(status) {
  if (status === "done") {
    return { label: "Graded", color: "green.600", bg: "rgba(72, 187, 120, 0.15)" };
  }
  if (status === "error") {
    return { label: "Failed", color: "red.600", bg: "rgba(229, 62, 62, 0.12)" };
  }
  if (status === "grading") {
    return { label: "Grading…", color: "orange.700", bg: "rgba(237, 137, 54, 0.15)" };
  }
  return { label: "Waiting", color: "var(--text-muted)", bg: "var(--surface-muted)" };
}

const BulkGradeModal = ({ isOpen, onClose, submissions, onComplete }) => {
  const dispatch = useDispatch();
  const markSchemes = useSelector((state) => state.markSchemes.markSchemes);
  const rows = Array.isArray(submissions) ? submissions : [];

  const [selectedMarkSchemeId, setSelectedMarkSchemeId] = useState(null);
  const [limitPages, setLimitPages] = useState(false);
  const [pageRange, setPageRange] = useState(DEFAULT_PAGE_RANGE);
  const [isGrading, setIsGrading] = useState(false);
  const [rowStatus, setRowStatus] = useState({});
  const {
    ocrGroups,
    gradingGroups,
    ocrModel,
    gradingModel,
    onOcrModelChange,
    onGradingModelChange,
    isLoadingModels,
  } = useGraderModelSelection(isOpen);

  const markSchemeOptions = useMemo(
    () =>
      (Array.isArray(markSchemes) ? markSchemes : [])
        .filter(isRegisteredScheme)
        .map((scheme) => ({
          value: scheme.grader_scheme_id,
          label: scheme.title || scheme.name || `Scheme #${scheme.id}`,
        })),
    [markSchemes]
  );

  useEffect(() => {
    if (!isOpen) return;
    dispatch(getMarkSchemesBySubjectRequest());
    setSelectedMarkSchemeId(null);
    setLimitPages(false);
    setPageRange(DEFAULT_PAGE_RANGE);
    setIsGrading(false);
    setRowStatus({});
  }, [isOpen, dispatch]);

  const handleClose = () => {
    if (isGrading) return;
    onClose();
  };

  const handleGrade = async () => {
    if (isGrading) return;
    if (!selectedMarkSchemeId) {
      toast.error("Select a registered mark scheme.");
      return;
    }
    if (!rows.length) {
      toast.error("No submissions selected.");
      return;
    }

    setIsGrading(true);
    const initialStatus = {};
    rows.forEach((row) => {
      initialStatus[row.id] = { status: "grading" };
    });
    setRowStatus(initialStatus);

    const results = await Promise.all(
      rows.map(async (row) => {
        const url = examSubmissionPdfUrl(row.student_pdf);
        if (!url) {
          const error = "Student PDF URL is required.";
          setRowStatus((prev) => ({ ...prev, [row.id]: { status: "error", error } }));
          return { studentExam: row, aiPayload: null, error };
        }

        const body = buildMarkPaperBody({
          markSchemeId: selectedMarkSchemeId,
          url,
          ocrModel,
          gradingModel,
          pages: limitPages ? { from: pageRange[0], to: pageRange[1] } : undefined,
        });

        try {
          const result = await markPaperWithAi(body);
          const payload = normalizeAiPayload(result.data) ?? result.data;
          setRowStatus((prev) => ({ ...prev, [row.id]: { status: "done" } }));
          return { studentExam: row, aiPayload: payload, error: null };
        } catch (error) {
          const details = error?.response?.data ?? {
            message: error?.message || "Failed to correct with AI",
          };
          const message = details?.error || details?.message || "Failed to correct with AI";
          setRowStatus((prev) => ({ ...prev, [row.id]: { status: "error", error: message } }));
          return { studentExam: row, aiPayload: null, error: message };
        }
      })
    );

    setIsGrading(false);
    const succeeded = results.filter((item) => !item.error).length;
    const failed = results.length - succeeded;
    if (succeeded) {
      toast.success(
        failed
          ? `AI graded ${succeeded} of ${results.length} submissions`
          : `AI graded ${succeeded} submission${succeeded === 1 ? "" : "s"}`
      );
    } else {
      toast.error("AI grading failed for all selected submissions.");
    }
    onComplete?.(results, selectedMarkSchemeId);
  };

  const doneCount = rows.filter((row) => rowStatus[row.id]?.status === "done").length;
  const errorCount = rows.filter((row) => rowStatus[row.id]?.status === "error").length;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent
        {...adminModalContentProps}
        bg="var(--surface-raised)"
        color="var(--text-primary)"
      >
        <ModalHeader {...adminModalHeaderProps}>
          Grade selected submissions
        </ModalHeader>
        {!isGrading ? <ModalCloseButton color="var(--text-primary)" /> : null}
        <ModalBody {...adminModalBodyProps}>
          <Stack spacing={5}>
            <Text fontSize="sm" color="var(--text-muted)">
              Choose a mark scheme, then the AI grader will run in parallel for up to{" "}
              {rows.length} submission{rows.length === 1 ? "" : "s"}. You will review and
              submit each result one by one.
            </Text>

            <Box>
              <FormLabel fontSize="xs" fontWeight="700" mb={1}>
                Mark scheme
              </FormLabel>
              <Select
                styles={modalSelectStyles}
                options={markSchemeOptions}
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                menuPosition="fixed"
                value={markSchemeOptions.find((opt) => opt.value === selectedMarkSchemeId) || null}
                onChange={(opt) => setSelectedMarkSchemeId(opt?.value ?? null)}
                placeholder={
                  markSchemeOptions.length
                    ? "Select registered mark scheme"
                    : "No registered mark schemes"
                }
                isClearable
                isDisabled={!markSchemeOptions.length || isGrading}
              />
            </Box>

            <GraderModelFields
              ocrGroups={ocrGroups}
              gradingGroups={gradingGroups}
              ocrModel={ocrModel}
              gradingModel={gradingModel}
              onOcrModelChange={onOcrModelChange}
              onGradingModelChange={onGradingModelChange}
              isDisabled={isGrading}
              isLoading={isLoadingModels}
            />

            <Checkbox
              isChecked={limitPages}
              onChange={(e) => setLimitPages(e.target.checked)}
              colorScheme="red"
              fontWeight="700"
              fontSize="sm"
              isDisabled={isGrading}
            >
              Limit pages (optional)
            </Checkbox>

            {limitPages ? (
              <Box>
                <Flex justify="space-between" mb={2}>
                  <Text fontSize="xs" fontWeight="700" color="var(--text-muted)">
                    Pages
                  </Text>
                  <Text fontSize="xs" fontWeight="800" color="var(--text-primary)">
                    {pageRange[0]} – {pageRange[1]}
                  </Text>
                </Flex>
                <RangeSlider
                  min={1}
                  max={50}
                  step={1}
                  value={pageRange}
                  onChange={setPageRange}
                  colorScheme="red"
                  isDisabled={isGrading}
                >
                  <RangeSliderTrack>
                    <RangeSliderFilledTrack bg={brandPalette.primary} />
                  </RangeSliderTrack>
                  <RangeSliderThumb index={0} boxSize={4} />
                  <RangeSliderThumb index={1} boxSize={4} />
                </RangeSlider>
              </Box>
            ) : null}

            <Box>
              <Flex align="center" justify="space-between" mb={2}>
                <Text fontSize="xs" fontWeight="800" color="var(--text-muted)" textTransform="uppercase">
                  Submissions
                </Text>
                {isGrading || doneCount || errorCount ? (
                  <Text fontSize="xs" fontWeight="700" color="var(--text-muted)">
                    {doneCount} done{errorCount ? ` · ${errorCount} failed` : ""}
                  </Text>
                ) : null}
              </Flex>
              <Stack spacing={2}>
                {rows.map((row) => {
                  const status = rowStatus[row.id]?.status || "idle";
                  const meta = statusMeta(status);
                  return (
                    <Flex
                      key={row.id}
                      align="center"
                      justify="space-between"
                      gap={3}
                      p={3}
                      borderWidth="1px"
                      borderColor="var(--border-subtle)"
                      borderRadius="lg"
                      bg="var(--surface-muted)"
                    >
                      <Text fontSize="sm" fontWeight="700" color="var(--text-primary)">
                        {studentLabel(row)}
                      </Text>
                      <HStack spacing={2}>
                        {status === "grading" ? (
                          <Spinner size="sm" color={brandPalette.primary} />
                        ) : status === "done" ? (
                          <MdCheck color="#38A169" size={18} />
                        ) : status === "error" ? (
                          <MdClose color="#E53E3E" size={18} />
                        ) : null}
                        <Badge
                          px={2}
                          py={0.5}
                          borderRadius="md"
                          fontWeight="700"
                          fontSize="xs"
                          bg={meta.bg}
                          color={meta.color}
                          title={rowStatus[row.id]?.error || undefined}
                        >
                          {meta.label}
                        </Badge>
                      </HStack>
                    </Flex>
                  );
                })}
              </Stack>
            </Box>
          </Stack>
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="ghost" onClick={handleClose} fontWeight="700" isDisabled={isGrading}>
            Cancel
          </Button>
          <Button
            bg={brandPalette.primary}
            color="white"
            fontWeight="700"
            onClick={handleGrade}
            isLoading={isGrading}
            loadingText="Grading…"
            isDisabled={!rows.length || !markSchemeOptions.length || isGrading}
            _hover={{ bg: brandPalette.secondary }}
          >
            Grade {rows.length} submission{rows.length === 1 ? "" : "s"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default BulkGradeModal;
